import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, MarkerType, Handle, Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowLeft, Save, Play, Trash2, Plus, PlayCircle, GitBranch,
  User, LogIn, LogOut, FileOutput, Check, AlertTriangle,
  Gavel, Swords, GitMerge, UserCheck, Wrench, BookOpen, Route
} from "lucide-react";
import { workflowsApi } from "../../lib/workflows";
import { workforceApi } from "../../lib/workforce";
import api from "../../lib/api";

/* -------------------- Custom node components -------------------- */
const NodeShell = memo(({ tone = "slate", icon: Icon, title, subtitle, running, done, failed, selected, children, showIn = true, showOut = true, outHandles = null }) => {
  const tones = {
    slate:  { bg: "#F1F5F9", accent: "#0F172A" },
    blue:   { bg: "#DBEAFE", accent: "#1D4ED8" },
    amber:  { bg: "#FEF3C7", accent: "#B45309" },
    green:  { bg: "#D1FAE5", accent: "#047857" },
    red:    { bg: "#FEE2E2", accent: "#B91C1C" },
    violet: { bg: "#EDE9FE", accent: "#6D28D9" },
    pink:   { bg: "#FCE7F3", accent: "#BE185D" },
    orange: { bg: "#FFEDD5", accent: "#C2410C" },
  };
  const t = tones[tone] || tones.slate;
  const handles = outHandles && outHandles.length > 0 ? outHandles : (showOut ? [{ id: null, label: null, color: t.accent }] : []);
  return (
    <div
      className={`brut-card px-3 py-2.5 min-w-[190px] transition-all relative ${selected ? "ring-2 ring-amber-400" : ""}`}
      style={{ background: t.bg, borderColor: t.accent, boxShadow: `4px 4px 0 ${t.accent}` }}
    >
      {showIn && <Handle type="target" position={Position.Left} style={{ background: t.accent, width: 10, height: 10, border: "2px solid #0F172A" }} />}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-lg border-2 border-slate-900 bg-white flex items-center justify-center" style={{ color: t.accent }}>
          {Icon && <Icon size={14} strokeWidth={2.5} />}
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-sm leading-tight truncate">{title}</div>
          {subtitle && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold truncate">{subtitle}</div>}
        </div>
        {running && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500 dot-blink" />}
        {done && !running && <Check size={12} className="ml-auto text-emerald-600" strokeWidth={3} />}
        {failed && <AlertTriangle size={12} className="ml-auto text-red-600" strokeWidth={3} />}
      </div>
      {children && <div className="text-xs text-slate-700">{children}</div>}
      {handles.map((h, i) => {
        const total = handles.length;
        const top = total === 1 ? "50%" : `${((i + 1) / (total + 1)) * 100}%`;
        return (
          <React.Fragment key={h.id || `handle-${i}`}>
            <Handle
              type="source"
              position={Position.Right}
              id={h.id || undefined}
              style={{
                top, background: h.color || t.accent, width: 10, height: 10,
                border: "2px solid #0F172A",
              }}
            />
            {h.label && (
              <div
                className="absolute text-[9px] uppercase tracking-widest font-bold px-1 rounded-full border border-slate-900 bg-white"
                style={{ top: `calc(${top} - 8px)`, right: -8, transform: "translateX(100%)", color: h.color || t.accent }}
              >
                {h.label}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

const StartNode  = memo(({ data, selected }) => <NodeShell tone="green"  icon={LogIn}      title="Start"  subtitle="Workflow input" selected={selected} showIn={false} running={data.running} done={data.done} failed={data.failed} />);
const OutputNode = memo(({ data, selected }) => <NodeShell tone="amber"  icon={FileOutput} title="Output" subtitle="Final result"   selected={selected} showOut={false} running={data.running} done={data.done} failed={data.failed} />);
const EndNode    = memo(({ data, selected }) => <NodeShell tone="red"    icon={LogOut}     title="End"    subtitle="Terminate"      selected={selected} showOut={false} running={data.running} done={data.done} failed={data.failed} />);
const WorkerNode = memo(({ data, selected }) => {
  const w = data.workerName || "Pick a worker…";
  return (
    <NodeShell tone="blue" icon={User} title={w} subtitle={data.brainName || "Uses worker's brain"} selected={selected} running={data.running} done={data.done} failed={data.failed}>
      {data.task && <div className="mt-1 text-[10px] text-slate-600 line-clamp-2 italic">"{data.task}"</div>}
    </NodeShell>
  );
});
const ReviewerNode = memo(({ data, selected }) => (
  <NodeShell
    tone="violet" icon={Gavel} title={data.workerName || "Reviewer…"}
    subtitle={data.brainName || "Uses reviewer's brain"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
    outHandles={[
      { id: "pass",   label: "PASS",   color: "#059669" },
      { id: "revise", label: "REVISE", color: "#D97706" },
      { id: "reject", label: "REJECT", color: "#DC2626" },
    ]}
  />
));
const ConditionNode = memo(({ data, selected }) => (
  <NodeShell
    tone="orange" icon={GitMerge} title="Condition"
    subtitle={data.conditionSummary || "if …"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
    outHandles={[
      { id: "true",  label: "TRUE",  color: "#059669" },
      { id: "false", label: "FALSE", color: "#DC2626" },
    ]}
  />
));
const HumanApprovalNode = memo(({ data, selected }) => (
  <NodeShell
    tone="pink" icon={UserCheck} title="Human Approval"
    subtitle={data.approvalSummary || "Pauses run"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
    outHandles={[
      { id: "approve", label: "APPROVE", color: "#059669" },
      { id: "revise",  label: "REVISE",  color: "#D97706" },
      { id: "reject",  label: "REJECT",  color: "#DC2626" },
    ]}
  />
));
const DebateNode = memo(({ data, selected }) => (
  <NodeShell
    tone="red" icon={Swords} title="Debate"
    subtitle={data.debateSummary || "Debaters + Judge"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
  />
));
const ToolNode = memo(({ data, selected }) => (
  <NodeShell
    tone="slate" icon={Wrench} title="Tool"
    subtitle={data.toolSummary || "Pick a tool…"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
  />
));
const KnowledgeNode = memo(({ data, selected }) => (
  <NodeShell
    tone="green" icon={BookOpen} title="Knowledge Search"
    subtitle={data.knowledgeSummary || "Search your library"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
  />
));
const RouterNode = memo(({ data, selected }) => (
  <NodeShell
    tone="violet" icon={Route} title={data.workerName || "Router…"}
    subtitle={data.routerSummary || "Pick brain by rule"} selected={selected}
    running={data.running} done={data.done} failed={data.failed}
  />
));

const NODE_TYPES = {
  start: StartNode, worker: WorkerNode, output: OutputNode, end: EndNode,
  reviewer: ReviewerNode, condition: ConditionNode,
  human_approval: HumanApprovalNode, debate: DebateNode,
  tool: ToolNode, knowledge: KnowledgeNode, router: RouterNode,
};

/* -------------------- Editor -------------------- */
export default function WorkflowEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [wf, setWf] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [brains, setBrains] = useState({});
  const [mcpTools, setMcpTools] = useState([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrs, setValidationErrs] = useState([]);
  const [runInput, setRunInput] = useState("");
  const [running, setRunning] = useState(false);
  const [name, setName] = useState("");
  const saveTimer = useRef(null);

  // Load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [w, wsRes, bsRes] = await Promise.all([
        workflowsApi.get(id),
        workforceApi.listWorkers(),
        workforceApi.listBrains(),
      ]);
      api.get("/mcp_tools").then((r) => { if (!cancelled) setMcpTools(r.data.tools || []); }).catch(() => {});
      if (cancelled) return;
      setWf(w);
      setName(w.name);
      const bMap = {}; (bsRes.brains || []).forEach((b) => { bMap[b.id] = b; });
      setBrains(bMap);
      setWorkers(wsRes.workers || []);
      const rfNodes = (w.nodes || []).map((n) => toRfNode(n, wsRes.workers || [], bMap));
      const rfEdges = (w.edges || []).map((e) => ({
        id: e.edge_id, source: e.source, target: e.target,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#0F172A" },
        style: { stroke: "#0F172A", strokeWidth: 2 },
      }));
      setNodes(rfNodes);
      setEdges(rfEdges);
    })();
    return () => { cancelled = true; };
  }, [id, setNodes, setEdges]);

  const save = useCallback(async () => {
    if (!wf) return;
    setSaving(true);
    try {
      const payload = {
        name: name || "Untitled workflow",
        description: wf.description || "",
        nodes: nodes.map((n) => ({
          node_id: n.id,
          type: n.type,
          position: n.position,
          config: n.data?.config || {},
        })),
        edges: edges.map((e) => ({
          edge_id: e.id, source: e.source, target: e.target,
          source_handle: e.sourceHandle, target_handle: e.targetHandle,
        })),
        variables: wf.variables || [],
        settings: wf.settings || {},
      };
      const fresh = await workflowsApi.update(id, payload);
      setWf(fresh);
      setDirty(false);
    } catch (e) {
      console.error("Failed to save workflow:", e);
    } finally {
      setSaving(false);
    }
  }, [wf, name, nodes, edges, id]);

  // Auto-save debounced
  useEffect(() => {
    if (!wf) return;
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 900);
    return () => clearTimeout(saveTimer.current);
  }, [wf, save]);

  const onConnect = useCallback(
    (conn) => setEdges((eds) => addEdge({
      ...conn, id: `e_${Math.random().toString(36).slice(2, 8)}`,
      markerEnd: { type: MarkerType.ArrowClosed, color: "#0F172A" },
      style: { stroke: "#0F172A", strokeWidth: 2 },
    }, eds)),
    [setEdges]
  );

  const addNode = (type) => {
    const id_ = `${type}_${Math.random().toString(36).slice(2, 8)}`;
    const defaults = {
      output: { format: "markdown" },
      condition: { left: "{{workflow.input}}", op: "contains", right: "" },
      reviewer: { criteria: ["Accuracy", "Completeness", "Clarity"], instructions: "Evaluate against the criteria." },
      debate: { debaters: [], judge_worker_id: "", rounds: 2, topic_template: "{{workflow.input}}" },
      human_approval: { prompt: "Approve to continue" },
      tool: { tool: "web_search", query: "{{workflow.input}}" },
      knowledge: { query: "{{workflow.input}}", max_results: 5 },
      router: {
        task_instructions: "{{workflow.input}}",
        default_brain_id: "gpt-4o-mini",
        match_against: "task",
        routes: [
          { label: "code",  keywords: ["code", "python", "bug", "error", "script"], brain_id: "deepseek-chat" },
          { label: "prose", keywords: ["write", "draft", "email", "memo", "post"],  brain_id: "claude-sonnet-4-6" },
        ],
      },
    };
    const conf = defaults[type] || {};
    const cx = 300 + Math.random() * 200;
    const cy = 150 + Math.random() * 200;
    const rfNode = toRfNode(
      { node_id: id_, type, position: { x: cx, y: cy }, config: conf },
      workers, brains
    );
    setNodes((nds) => [...nds, rfNode]);
  };

  const onSelect = useCallback((_evt, node) => setSelected(node), []);
  const onPaneClick = useCallback(() => setSelected(null), []);

  const updateSelectedConfig = (partial) => {
    if (!selected) return;
    setNodes((nds) => nds.map((n) => {
      if (n.id !== selected.id) return n;
      const cfg = { ...(n.data.config || {}), ...partial };
      const updated = toRfNode(
        { node_id: n.id, type: n.type, position: n.position, config: cfg },
        workers, brains,
        n.data
      );
      return updated;
    }));
    setSelected((s) => s ? { ...s, data: { ...s.data, config: { ...(s.data.config || {}), ...partial } } } : s);
  };

  const deleteSelected = () => {
    if (!selected) return;
    setNodes((nds) => nds.filter((n) => n.id !== selected.id));
    setEdges((eds) => eds.filter((e) => e.source !== selected.id && e.target !== selected.id));
    setSelected(null);
  };

  const validate = async () => {
    // Save first to ensure server sees latest
    await save();
    const r = await workflowsApi.validate(id);
    setValidationErrs(r.errors || []);
    return r.valid;
  };

  const runWorkflow = async () => {
    setRunning(true);
    try {
      await save();
      const r = await workflowsApi.validate(id);
      if (!r.valid) {
        setValidationErrs(r.errors);
        return;
      }
      const started = await workflowsApi.run(id, { input: runInput });
      nav(`/workforce/runs/${started.run_id}`);
    } catch (e) {
      alert(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  };

  if (!wf) return <div className="p-10 text-sm text-slate-400">Loading workflow…</div>;

  return (
    <div className="h-[calc(100vh-118px)] flex flex-col" data-testid="workflow-editor">
      {/* Top toolbar */}
      <div className="border-b-2 border-slate-900 bg-white px-4 py-3 flex items-center gap-3 flex-wrap">
        <Link to="/workforce/workflows" className="text-slate-500 hover:text-slate-900" data-testid="back-to-workflows">
          <ArrowLeft size={18} strokeWidth={2.5} />
        </Link>
        <div className="flex items-center gap-2 min-w-0">
          <GitBranch size={16} strokeWidth={2.5} />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="font-heading font-bold text-lg bg-transparent border-b-2 border-transparent focus:border-slate-900 outline-none px-1 min-w-[240px]"
            data-testid="workflow-name-input"
          />
        </div>
        <div className="text-xs text-slate-500 font-mono">
          {saving ? "saving…" : dirty ? "unsaved" : "saved"}
        </div>
        <div className="flex-1" />
        <input
          value={runInput}
          onChange={(e) => setRunInput(e.target.value)}
          placeholder="Workflow input (e.g. Should we enter the contractor market?)"
          className="brut-input py-1.5 min-w-[280px]"
          data-testid="run-input"
        />
        <button onClick={validate} className="brut-btn brut-btn-outline text-sm" data-testid="validate-btn">
          Validate
        </button>
        <button onClick={runWorkflow} disabled={running} className="brut-btn text-sm" data-testid="run-btn">
          <PlayCircle size={14} strokeWidth={2.5} /> {running ? "Starting…" : "Run"}
        </button>
        <Link to="/workforce/runs" className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2 font-heading font-bold" data-testid="run-history-link">
          Run history
        </Link>
      </div>

      {validationErrs.length > 0 && (
        <div className="px-4 py-2 bg-red-100 border-b-2 border-red-500 text-red-800 text-sm flex items-center gap-2" data-testid="validation-errors">
          <AlertTriangle size={14} strokeWidth={3} />
          <b>Cannot run:</b>
          <span>{validationErrs.join(" · ")}</span>
        </div>
      )}

      {/* 3-column layout: palette + canvas + inspector */}
      <div className="flex-1 grid grid-cols-[220px_minmax(0,1fr)_320px] min-h-0">
        {/* Left palette */}
        <div className="border-r-2 border-slate-900 bg-[#FDF8F5] p-4 overflow-y-auto" data-testid="node-palette">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Workers & work</div>
          <div className="space-y-2">
            <PaletteItem label="Worker"    icon={User}      tone="blue"   onAdd={() => addNode("worker")}         testid="add-worker" />
            <PaletteItem label="Debate"    icon={Swords}    tone="red"    onAdd={() => addNode("debate")}         testid="add-debate" />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-4 mb-2">Control</div>
          <div className="space-y-2">
            <PaletteItem label="Reviewer"  icon={Gavel}     tone="violet" onAdd={() => addNode("reviewer")}       testid="add-reviewer" />
            <PaletteItem label="Approval"  icon={UserCheck} tone="pink"   onAdd={() => addNode("human_approval")} testid="add-approval" />
            <PaletteItem label="Condition" icon={GitMerge}  tone="orange" onAdd={() => addNode("condition")}      testid="add-condition" />
            <PaletteItem label="Router"    icon={Route}     tone="violet" onAdd={() => addNode("router")}         testid="add-router" />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-4 mb-2">Data & Actions</div>
          <div className="space-y-2">
            <PaletteItem label="Tool"      icon={Wrench}   tone="slate" onAdd={() => addNode("tool")}      testid="add-tool" />
            <PaletteItem label="Knowledge" icon={BookOpen} tone="green" onAdd={() => addNode("knowledge")} testid="add-knowledge" />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-4 mb-2">Terminals</div>
          <div className="space-y-2">
            <PaletteItem label="Output" icon={FileOutput} tone="amber" onAdd={() => addNode("output")} testid="add-output" />
            <PaletteItem label="End"    icon={LogOut}     tone="red"   onAdd={() => addNode("end")}    testid="add-end" />
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Coming later</div>
          <div className="space-y-1.5 text-xs text-slate-400">
            <div>· MCP</div>
          </div>
        </div>

        {/* Canvas */}
        <div className="relative bg-[#FBF7F2]" data-testid="workflow-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onSelect}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            fitView
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{
              markerEnd: { type: MarkerType.ArrowClosed, color: "#0F172A" },
              style: { stroke: "#0F172A", strokeWidth: 2 },
            }}
          >
            <Background gap={20} size={1.5} color="#0F172A" style={{ opacity: 0.08 }} />
            <Controls showInteractive={false} />
            <MiniMap zoomable pannable style={{ border: "2px solid #0F172A", borderRadius: 8 }} />
          </ReactFlow>
        </div>

        {/* Right inspector */}
        <div className="border-l-2 border-slate-900 bg-white p-4 overflow-y-auto" data-testid="inspector">
          {!selected ? (
            <div className="text-sm text-slate-400 italic">
              Click a node to configure it.
            </div>
          ) : (
            <Inspector
              node={selected}
              workers={workers}
              brains={brains}
              nodes={nodes}
              mcpTools={mcpTools}
              onChange={updateSelectedConfig}
              onDelete={deleteSelected}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PaletteItem({ label, icon: Icon, tone, onAdd, testid }) {
  const colors = {
    blue: "#1D4ED8", amber: "#B45309", green: "#047857", red: "#B91C1C", slate: "#0F172A",
    violet: "#6D28D9", pink: "#BE185D", orange: "#C2410C",
  };
  return (
    <button
      onClick={onAdd}
      data-testid={testid}
      className="w-full flex items-center gap-2 brut-card px-3 py-2 text-sm hover:-translate-y-0.5 transition-transform"
      style={{ borderColor: colors[tone], boxShadow: `3px 3px 0 ${colors[tone]}` }}
    >
      <Icon size={14} strokeWidth={2.5} style={{ color: colors[tone] }} />
      <span className="font-heading font-bold">{label}</span>
      <Plus size={12} className="ml-auto text-slate-400" strokeWidth={3} />
    </button>
  );
}

/* -------------------- Inspector -------------------- */
function Inspector({ node, workers, brains, nodes, mcpTools = [], onChange, onDelete }) {
  const cfg = node.data?.config || {};
  return (
    <div className="space-y-3" data-testid="inspector-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          {node.type} node
        </div>
        {node.type !== "start" && (
          <button onClick={onDelete} className="text-slate-400 hover:text-red-500" data-testid="delete-node">
            <Trash2 size={14} strokeWidth={2.5} />
          </button>
        )}
      </div>
      <div className="text-xs text-slate-500 font-mono break-all">{node.id}</div>

      {node.type === "start" && (
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Input prompt template (optional)</label>
          <textarea
            rows={3}
            value={cfg.prompt_template || ""}
            onChange={(e) => onChange({ prompt_template: e.target.value })}
            className="brut-input text-sm"
            placeholder="Leave blank to use the runtime input as-is."
            data-testid="start-tpl"
          />
        </div>
      )}

      {node.type === "worker" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Worker</label>
            <select
              value={cfg.worker_id || ""}
              onChange={(e) => onChange({ worker_id: e.target.value })}
              className="brut-input text-sm"
              data-testid="worker-select"
            >
              <option value="">— Pick a worker —</option>
              {workers.map((w) => (
                <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name} — ${w.title}`}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Task instructions</label>
            <textarea
              rows={5}
              value={cfg.task_instructions || ""}
              onChange={(e) => onChange({ task_instructions: e.target.value })}
              className="brut-input text-sm"
              placeholder="What should this worker do? Reference upstream nodes with {{nodes.node_id.output}} or the input with {{workflow.input}}."
              data-testid="worker-task"
            />
            {nodes.length > 1 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {nodes
                  .filter((n) => n.id !== node.id)
                  .slice(0, 6)
                  .map((n) => (
                    <button
                      key={n.id}
                      onClick={() => onChange({ task_instructions: (cfg.task_instructions || "") + ` {{nodes.${n.id}.output}}` })}
                      className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border border-slate-400 bg-slate-50 hover:bg-amber-100"
                      data-testid={`ref-${n.id}`}
                    >
                      + {n.id}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Brain</label>
            <select
              value={cfg.brain_override?.brain_id || ""}
              onChange={(e) => onChange({ brain_override: e.target.value ? { brain_id: e.target.value } : null })}
              className="brut-input text-sm"
              data-testid="brain-override"
            >
              <option value="">Use worker's brain</option>
              {Object.values(brains).map((b) => (
                <option key={b.id} value={b.id}>{`${b.name} (${b.family})`}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {node.type === "output" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Format</label>
            <select
              value={cfg.format || "markdown"}
              onChange={(e) => onChange({ format: e.target.value })}
              className="brut-input text-sm"
              data-testid="output-format"
            >
              <option value="markdown">Markdown</option>
              <option value="text">Plain text</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Source (optional)</label>
            <select
              value={cfg.source_node_id || ""}
              onChange={(e) => onChange({ source_node_id: e.target.value || null })}
              className="brut-input text-sm"
              data-testid="output-source"
            >
              <option value="">Last worker output</option>
              {nodes.filter((n) => ["worker", "debate", "reviewer"].includes(n.type)).map((n) => (
                <option key={n.id} value={n.id}>{n.data?.workerName || n.id}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {node.type === "reviewer" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Reviewer worker</label>
            <select value={cfg.worker_id || ""} onChange={(e) => onChange({ worker_id: e.target.value })}
                    className="brut-input text-sm" data-testid="reviewer-worker">
              <option value="">— Pick a reviewer —</option>
              {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name} — ${w.title}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Review target (optional)</label>
            <input value={cfg.review_target || ""} onChange={(e) => onChange({ review_target: e.target.value })}
                   placeholder="{{nodes.writer.output}} — leave blank for last output"
                   className="brut-input py-1.5 text-sm" data-testid="reviewer-target" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Instructions</label>
            <textarea rows={2} value={cfg.instructions || ""} onChange={(e) => onChange({ instructions: e.target.value })}
                      className="brut-input text-sm" data-testid="reviewer-instructions" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Criteria (one per line)</label>
            <textarea rows={3} value={(cfg.criteria || []).join("\n")}
                      onChange={(e) => onChange({ criteria: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                      className="brut-input text-sm" data-testid="reviewer-criteria" />
          </div>
          <BrainOverride cfg={cfg} brains={brains} onChange={onChange} testid="reviewer-brain" />
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Wire three outgoing edges from PASS / REVISE / REJECT handles. REVISE typically loops back to the writer.
          </div>
        </>
      )}

      {node.type === "condition" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Left value (template)</label>
            <input value={cfg.left || ""} onChange={(e) => onChange({ left: e.target.value })}
                   placeholder="{{nodes.reviewer_1.output.score}}"
                   className="brut-input py-1.5 text-sm" data-testid="cond-left" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Operator</label>
            <select value={cfg.op || "eq"} onChange={(e) => onChange({ op: e.target.value })}
                    className="brut-input text-sm" data-testid="cond-op">
              {[["eq","="],["neq","≠"],["contains","contains"],["not_contains","not contains"],
                ["gt",">"],["gte","≥"],["lt","<"],["lte","≤"],["exists","exists"],["not_exists","not exists"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {!["exists", "not_exists"].includes(cfg.op) && (
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Right value</label>
              <input value={cfg.right ?? ""} onChange={(e) => onChange({ right: e.target.value })}
                     className="brut-input py-1.5 text-sm" data-testid="cond-right" />
            </div>
          )}
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Wire two outgoing edges from TRUE / FALSE handles.
          </div>
        </>
      )}

      {node.type === "human_approval" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Prompt shown to human</label>
            <textarea rows={3} value={cfg.prompt || ""} onChange={(e) => onChange({ prompt: e.target.value })}
                      className="brut-input text-sm" data-testid="approval-prompt" />
          </div>
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Wire three outgoing edges: APPROVE, REVISE, REJECT. During a run, the human clicks a button in Run detail.
          </div>
        </>
      )}

      {node.type === "debate" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Topic template</label>
            <input value={cfg.topic_template || ""} onChange={(e) => onChange({ topic_template: e.target.value })}
                   className="brut-input py-1.5 text-sm" data-testid="debate-topic" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Debaters (2–5)</label>
            <DebaterList
              debaters={cfg.debaters || []} workers={workers} brains={brains}
              onChange={(list) => onChange({ debaters: list })}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Judge</label>
            <select value={cfg.judge_worker_id || ""} onChange={(e) => onChange({ judge_worker_id: e.target.value })}
                    className="brut-input text-sm" data-testid="debate-judge">
              <option value="">— Pick a judge —</option>
              {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Judge brain (optional)</label>
            <select value={cfg.judge_brain_override?.brain_id || ""}
                    onChange={(e) => onChange({ judge_brain_override: e.target.value ? { brain_id: e.target.value } : null })}
                    className="brut-input text-sm" data-testid="debate-judge-brain">
              <option value="">Use judge's brain</option>
              {Object.values(brains).map((b) => <option key={b.id} value={b.id}>{`${b.name} (${b.family})`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Rounds</label>
            <div className="flex gap-1.5">
              {[1,2,3].map((r) => (
                <button key={r} onClick={() => onChange({ rounds: r })}
                        className={`flex-1 px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 ${
                          (cfg.rounds || 2) === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                        }`}
                        data-testid={`debate-rounds-${r}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {node.type === "tool" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Tool</label>
            <select value={cfg.tool || "web_search"} onChange={(e) => onChange({ tool: e.target.value })}
                    className="brut-input text-sm" data-testid="tool-kind">
              <option value="web_search">Web Search (live, grounded)</option>
              <option value="calculator">Calculator (numeric expression)</option>
              <option value="webhook">Webhook (POST / GET)</option>
              <option value="mcp">Connected tool (Notion, GitHub, Jira…)</option>
            </select>
          </div>
          {cfg.tool === "mcp" && (
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Saved tool</label>
              <select value={cfg.mcp_tool_id || ""} onChange={(e) => onChange({ mcp_tool_id: e.target.value })}
                      className="brut-input text-sm" data-testid="tool-mcp-id">
                <option value="">— pick a tool —</option>
                {mcpTools.map((t) => <option key={t.tool_id} value={t.tool_id}>{t.provider} · {t.name}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">
                Manage connectors in <Link to="/workforce/tools" className="underline">Workforce → Tools</Link>. The tool's body template receives <code>{"{{workflow.input}}"}</code>.
              </div>
            </div>
          )}
          {cfg.tool !== "mcp" && (
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">
              {cfg.tool === "calculator" ? "Expression" : cfg.tool === "webhook" ? "Payload content" : "Query"}
            </label>
            <textarea rows={3} value={cfg.query || ""} onChange={(e) => onChange({ query: e.target.value })}
                      className="brut-input text-sm"
                      placeholder={cfg.tool === "calculator" ? "2 * (3 + 4)" : "{{workflow.input}} or {{nodes.node_id.output}}"}
                      data-testid="tool-query" />
          </div>
          )}
          {cfg.tool === "webhook" && (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">URL</label>
                <input value={cfg.url || ""} onChange={(e) => onChange({ url: e.target.value })}
                       placeholder="https://example.com/hook"
                       className="brut-input py-1.5 text-sm" data-testid="tool-url" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Method</label>
                <select value={cfg.method || "POST"} onChange={(e) => onChange({ method: e.target.value })}
                        className="brut-input text-sm" data-testid="tool-method">
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>
              </div>
            </>
          )}
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Tools do real work: Web Search fetches live results, Calculator evaluates math, Webhook calls external URLs.
          </div>
        </>
      )}

      {node.type === "knowledge" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Query template</label>
            <textarea rows={3} value={cfg.query || ""} onChange={(e) => onChange({ query: e.target.value })}
                      className="brut-input text-sm"
                      placeholder="{{workflow.input}} — searches across your uploaded documents"
                      data-testid="knowledge-query" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Max results</label>
            <input type="number" min={1} max={20} value={cfg.max_results ?? 5}
                   onChange={(e) => onChange({ max_results: Math.max(1, Math.min(20, parseInt(e.target.value || "5", 10))) })}
                   className="brut-input py-1.5 text-sm" data-testid="knowledge-max" />
          </div>
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Ranks by keyword match count across your document library. Upload documents in the Document Q&A workspace first.
          </div>
        </>
      )}

      {node.type === "router" && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Worker (persona)</label>
            <select value={cfg.worker_id || ""} onChange={(e) => onChange({ worker_id: e.target.value })}
                    className="brut-input text-sm" data-testid="router-worker">
              <option value="">— Pick a worker —</option>
              {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name} — ${w.title}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Task instructions</label>
            <textarea rows={3} value={cfg.task_instructions || ""} onChange={(e) => onChange({ task_instructions: e.target.value })}
                      className="brut-input text-sm"
                      placeholder="{{workflow.input}} — the task is executed with the routed brain."
                      data-testid="router-task" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Match against</label>
            <select value={cfg.match_against || "task"} onChange={(e) => onChange({ match_against: e.target.value })}
                    className="brut-input text-sm" data-testid="router-match-against">
              <option value="task">Rendered task text</option>
              <option value="input">Raw workflow input</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Routing rules (first match wins)</label>
            <RouteList
              routes={cfg.routes || []} brains={brains}
              onChange={(list) => onChange({ routes: list })}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Default brain (fallback)</label>
            <select value={cfg.default_brain_id || ""} onChange={(e) => onChange({ default_brain_id: e.target.value })}
                    className="brut-input text-sm" data-testid="router-default-brain">
              <option value="">— Pick a fallback —</option>
              {Object.values(brains).map((b) => <option key={b.id} value={b.id}>{`${b.name} (${b.family})`}</option>)}
            </select>
          </div>
          <div className="text-[10px] text-slate-500 leading-snug pt-1">
            Rules are evaluated top-down. First rule with any keyword hit wins. Otherwise the fallback brain is used. The worker's persona always drives the response — only the brain changes.
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------- helpers -------------------- */
function BrainOverride({ cfg, brains, onChange, testid = "brain-override" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-slate-500 block mb-1">Brain override</label>
      <select
        value={cfg.brain_override?.brain_id || ""}
        onChange={(e) => onChange({ brain_override: e.target.value ? { brain_id: e.target.value } : null })}
        className="brut-input text-sm"
        data-testid={testid}
      >
        <option value="">Use worker's brain</option>
        {Object.values(brains).map((b) => (
          <option key={b.id} value={b.id}>{`${b.name} (${b.family})`}</option>
        ))}
      </select>
    </div>
  );
}

function RouteList({ routes, brains, onChange }) {
  const add = () => onChange([...routes, { _key: `r_${Math.random().toString(36).slice(2, 10)}`, label: "", keywords: [], brain_id: "" }]);
  const update = (i, patch) => onChange(routes.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => onChange(routes.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="route-list">
      {routes.map((r, i) => (
        <div key={r._key || `route-${i}`} className="brut-card p-2 space-y-1.5">
          <div className="flex gap-1.5">
            <input value={r.label || ""} onChange={(e) => update(i, { label: e.target.value })}
                   placeholder="Rule name (e.g. code)" className="brut-input py-1 text-xs flex-1"
                   data-testid={`route-label-${i}`} />
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500 px-1" data-testid={`route-remove-${i}`}>
              <Trash2 size={12} strokeWidth={2.5} />
            </button>
          </div>
          <input
            value={(r.keywords || []).join(", ")}
            onChange={(e) => update(i, { keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="Keywords (comma-separated)"
            className="brut-input py-1 text-xs"
            data-testid={`route-keywords-${i}`}
          />
          <select value={r.brain_id || ""} onChange={(e) => update(i, { brain_id: e.target.value })}
                  className="brut-input py-1 text-xs" data-testid={`route-brain-${i}`}>
            <option value="">— pick brain —</option>
            {Object.values(brains).map((b) => <option key={b.id} value={b.id}>{`${b.name}`}</option>)}
          </select>
        </div>
      ))}
      <button onClick={add} className="w-full brut-btn brut-btn-outline text-xs py-1.5" data-testid="add-route">
        <Plus size={12} strokeWidth={3} /> Add rule
      </button>
    </div>
  );
}

function DebaterList({ debaters, workers, brains, onChange }) {
  const add = () => {
    if (debaters.length >= 5) return;
    onChange([...debaters, { _key: `d_${Math.random().toString(36).slice(2, 10)}`, worker_id: "", stance: debaters.length === 0 ? "Argue FOR" : "Argue AGAINST", brain_override: null }]);
  };
  const update = (i, patch) => onChange(debaters.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const remove = (i) => onChange(debaters.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="debater-list">
      {debaters.map((d, i) => (
        <div key={d._key || `debater-${i}`} className="brut-card p-2 space-y-1.5">
          <div className="flex gap-1.5">
            <select value={d.worker_id} onChange={(e) => update(i, { worker_id: e.target.value })}
                    className="brut-input py-1 text-xs flex-1" data-testid={`debater-worker-${i}`}>
              <option value="">— worker —</option>
              {workers.map((w) => <option key={w.worker_id} value={w.worker_id}>{`${w.avatar || "👤"} ${w.name}`}</option>)}
            </select>
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-red-500 px-1" data-testid={`debater-remove-${i}`}>
              <Trash2 size={12} strokeWidth={2.5} />
            </button>
          </div>
          <input value={d.stance || ""} onChange={(e) => update(i, { stance: e.target.value })}
                 placeholder="Stance…" className="brut-input py-1 text-xs" data-testid={`debater-stance-${i}`} />
          <select value={d.brain_override?.brain_id || ""}
                  onChange={(e) => update(i, { brain_override: e.target.value ? { brain_id: e.target.value } : null })}
                  className="brut-input py-1 text-xs" data-testid={`debater-brain-${i}`}>
            <option value="">Use worker's brain</option>
            {Object.values(brains).map((b) => <option key={b.id} value={b.id}>{`${b.name}`}</option>)}
          </select>
        </div>
      ))}
      {debaters.length < 5 && (
        <button onClick={add} className="w-full brut-btn brut-btn-outline text-xs py-1.5" data-testid="add-debater">
          <Plus size={12} strokeWidth={3} /> Add debater
        </button>
      )}
    </div>
  );
}

function toRfNode(n, workers, brains, prevData = {}) {
  const cfg = n.config || {};
  let workerName, brainName;
  let conditionSummary, approvalSummary, debateSummary;

  if (n.type === "worker" || n.type === "reviewer" || n.type === "router") {
    const w = workers.find((x) => x.worker_id === cfg.worker_id);
    workerName = w ? `${w.avatar || "👤"} ${w.name}` : (n.type === "reviewer" ? "Reviewer…" : n.type === "router" ? "Router…" : "Pick a worker…");
    const bId = cfg.brain_override?.brain_id || (w ? (w.brain || {}).brain_id : null);
    const b = bId ? brains[bId] : null;
    brainName = cfg.brain_override?.brain_id
      ? `⚡ ${b?.name || bId}`
      : b ? `${b.name}` : "";
  }
  if (n.type === "condition") {
    const opLabel = { eq: "=", neq: "≠", contains: "contains", not_contains: "!contains",
                      gt: ">", gte: "≥", lt: "<", lte: "≤", exists: "exists", not_exists: "!exists" }[cfg.op || "eq"] || "?";
    const left = (cfg.left || "").length > 20 ? cfg.left.slice(0, 18) + "…" : cfg.left || "?";
    const right = String(cfg.right ?? "").slice(0, 18) || "";
    conditionSummary = `${left} ${opLabel} ${right}`;
  }
  if (n.type === "human_approval") {
    approvalSummary = (cfg.prompt || "Pauses run").slice(0, 40);
  }
  if (n.type === "debate") {
    const dc = (cfg.debaters || []).length;
    debateSummary = `${dc} debater${dc === 1 ? "" : "s"} · ${cfg.rounds || 2} rounds`;
  }
  let toolSummary, knowledgeSummary, routerSummary;
  if (n.type === "tool") {
    const kind = cfg.tool || "web_search";
    const label = kind === "web_search" ? "Web Search" : kind === "calculator" ? "Calculator" : kind === "mcp" ? "Connected tool" : "Webhook";
    const q = (cfg.query || "").slice(0, 34);
    toolSummary = q ? `${label} · ${q}${(cfg.query || "").length > 34 ? "…" : ""}` : label;
  }
  if (n.type === "knowledge") {
    const q = (cfg.query || "").slice(0, 34);
    knowledgeSummary = q ? `Search · ${q}${(cfg.query || "").length > 34 ? "…" : ""}` : "Search your library";
  }
  if (n.type === "router") {
    const nRoutes = (cfg.routes || []).length;
    const defB = cfg.default_brain_id ? (brains[cfg.default_brain_id]?.name || cfg.default_brain_id) : "no default";
    routerSummary = `${nRoutes} rule${nRoutes === 1 ? "" : "s"} · fallback ${defB}`;
  }
  return {
    id: n.node_id,
    type: n.type,
    position: n.position || { x: 0, y: 0 },
    data: {
      ...prevData,
      config: cfg,
      workerName,
      brainName,
      task: cfg.task_instructions,
      conditionSummary,
      approvalSummary,
      debateSummary,
      toolSummary,
      knowledgeSummary,
      routerSummary,
    },
  };
}
